<script lang="ts">
    // @ts-nocheck

    import { getRole } from "../utils/Utils";
    import { SharePreferencesPanelLinks } from "$/utils/SharePreferencesPanelLinks";
    import TopicRole from "./TopicRole.svelte";
    import AddTopicRole from "./AddTopicRole.svelte";
    import ShareInput from "./ShareInput.svelte";
    import PanelHeader from "./panels/_components/PanelHeader.svelte";
    import { createEventDispatcher } from "svelte";
    import {
        ActiveTopicStore,
        ActiveTopic,
        TopicAccessLevel,
    } from "$/stores/extension/ActiveTopicStore";
    import { TenantStore } from "$/stores/TenantStore";
    import { AppStore, AppType } from "$/stores/AppStore";
    import { redirect } from "@roxi/routify";
    import { ServiceConsts } from "$/services/ServiceConsts";
    import { ClusterStore } from "$/stores/ClusterStore";
    import PanelLoadingAnimation from "./panels/_components/PanelLoadingAnimation.svelte";

    export let topic;
    export let user;
    export let showShareButton = true;
    export let showDashboardButton = true;

    let role = TopicAccessLevel.NO_ACCESS;
    let ownerRole;
    let userRoles = [];
    let clusterRoles = [];
    let clusterOwnerRole;
    let isTenantMember = false;
    let canEditAsWorkspaceMember = false;

    const dispatch = createEventDispatcher();

    $: generalRoleValue =
        topic?.publicRole === TopicAccessLevel.NO_ACCESS
            ? TopicAccessLevel.NO_ACCESS
            : TopicAccessLevel.VIEWER;
    $: specificRoleValue =
        topic?.publicRole === TopicAccessLevel.NO_ACCESS
            ? TopicAccessLevel.VIEWER
            : topic?.publicRole;
    $: description = topic?.description ?? "";
    $: {
        if (topic) {
            role = getRole(
                topic,
                user?.id,
                $TenantStore?.tenants,
                $ClusterStore.clusters,
            );
            ownerRole = topic.roles.find(
                (r) => r.role === TopicAccessLevel.OWNER,
            );
            userRoles = topic.roles.filter(
                (r) => r.role !== TopicAccessLevel.OWNER,
            );
            isTenantMember = $TenantStore?.tenants?.some(
                (t) => t.id === topic.tenant?.id,
            );

            canEditAsWorkspaceMember = isTenantMember && !$TenantStore.active.personal;

            clusterRoles = ClusterStore.getTopicRoles(
                topic.id,
                user?.id,
                isTenantMember,
            );
            clusterOwnerRole = clusterRoles?.find(
                (r) => r.role === TopicAccessLevel.OWNER,
            );
        }
    }

    async function generalRoleChanged(event) {
        generalRoleValue = Number(event.target.value);
        await ActiveTopicStore.updatePublicRole(generalRoleValue);

        if (generalRoleValue === TopicAccessLevel.VIEWER)
            specificRoleValue = TopicAccessLevel.VIEWER;
    }

    async function specificRoleChanged(event) {
        specificRoleValue = Number(event.target.value);
        await ActiveTopicStore.updatePublicRole(specificRoleValue);
    }

    async function addUserRole(topicRole) {
        await ActiveTopicStore.createTopicRole(topicRole);
        TenantStore.addCollaborator(topicRole.user);
    }

    async function editUserRole(topicRoleId, topicRoleValue) {
        await ActiveTopicStore.editTopicRole(topicRoleId, topicRoleValue);
    }

    async function deleteUserRole(topicRoleId) {
        let role = userRoles.find((role) => role.id === topicRoleId);
        await ActiveTopicStore.deleteTopicRole(topicRoleId);
        await TenantStore.refreshCollaboratorsList();
        let userRoleInCluster = clusterRoles.find(
            (clusterRole) => clusterRole.user.id === role.user.id,
        );
        if (userRoleInCluster === undefined) {
            await ActiveTopicStore.fetchCollaborators();
        }
    }

    function upgradeToProClicked() {
        if ($AppStore.type === AppType.EXTENSION) {
            window.open(ServiceConsts.DASHBOARD_URL + "/payment", "_blank");
        } else {
            $redirect("/payment");
            dispatch("close");
        }
    }

    function descriptionInputChange(event) {
        let topic = {
            id: $ActiveTopic.id,
            description: event.target.innerText,
        };
        ActiveTopicStore.updateTopic(topic);
    }
</script>

<div class="share-preferences-panel">
    <PanelHeader title="Share preferences" on:close={() => dispatch("close")} />

    {#if !topic}
        <PanelLoadingAnimation />
    {:else}
        <div class="share-preferences-panel-content">
            {#if role >= 3 || description}
                <div class="users">
                    <h2 class="public-role-header">Description</h2>

                    {#key description}
                        <div class="users-list">
                            <div
                                class="text-area"
                                contenteditable={role >=
                                    TopicAccessLevel.EDITOR}
                                placeholder="Add a description"
                                on:focusout={descriptionInputChange}
                            >
                                {description}
                            </div>
                        </div>
                    {/key}
                </div>
            {/if}

            {#if clusterRoles?.length > 0}
                <div class="users">
                    <h2 class="public-role-header">
                        Cluster Members

                        <a href={SharePreferencesPanelLinks.cluster_members}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z"
                                />
                            </svg>
                        </a>
                    </h2>

                    <div class="users-list">
                        {#each clusterRoles as clusterRole (clusterRole.id)}
                            <TopicRole role={clusterRole} editable={false} />
                        {/each}
                    </div>
                </div>
            {/if}

            {#if userRoles?.length > 0 || ownerRole}
                <div class="users">
                    <h2 class="public-role-header">
                        Topic Members

                        <a
                            href={SharePreferencesPanelLinks.topic_members}
                            target="_blank"
                            rel="noreferrer"
                            class="public-role-header__question"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z"
                                />
                            </svg>
                        </a>
                    </h2>

                    <div class="users-list">
                        {#if ownerRole || canEditAsWorkspaceMember}
                            <TopicRole role={ownerRole} editable={false} />
                        {/if}

                        {#each userRoles as userRole (userRole.id)}
                            {#key userRole}
                                <TopicRole
                                    role={userRole}
                                    editable={role === TopicAccessLevel.OWNER || canEditAsWorkspaceMember}
                                    on:edit={(event) =>
                                        editUserRole(
                                            userRole.id,
                                            event.detail.role,
                                        )}
                                    on:delete={() =>
                                        deleteUserRole(userRole.id)}
                                />
                            {/key}
                        {/each}

                        {#if role === TopicAccessLevel.OWNER || canEditAsWorkspaceMember}
                            <AddTopicRole
                                owner={ownerRole?.user}
                                existingRoles={topic.roles}
                                addRole={(topicRole) => addUserRole(topicRole)}
                            />
                        {/if}
                    </div>
                </div>
            {/if}

            <div class="public-role">
                <h2 class="public-role-header">
                    General Access

                    <a
                        href="https://help.collabwriting.com/en/articles/7729184-how-to-share-topic-with-collaborators#h_32255ffc6d"
                        target="_blank"
                        rel="noreferrer"
                        class="public-role-header__question"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z"
                            />
                        </svg>
                    </a>
                </h2>
                <div class="public-role-settings">
                    <div class="public-role-settings__icon">
                        {#if generalRoleValue === TopicAccessLevel.NO_ACCESS}
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                stroke-width="2"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                />
                            </svg>
                        {:else}
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                stroke-width="2"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        {/if}
                    </div>

                    <div class="public-role-settings__general">
                        <select
                            class="public-role-settings__general-select"
                            value={generalRoleValue}
                            on:change={generalRoleChanged}
                            disabled={role !== TopicAccessLevel.OWNER}
                        >
                            <option value={TopicAccessLevel.NO_ACCESS}
                                >Restricted</option
                            >
                            <option value={TopicAccessLevel.VIEWER}
                                >Anyone with the link</option
                            >
                        </select>
                        <div class="public-role-settings__general-info">
                            {#if generalRoleValue === TopicAccessLevel.NO_ACCESS}
                                Only people with access can open
                            {:else if specificRoleValue === TopicAccessLevel.VIEWER}
                                Anyone with the link can view
                            {:else if specificRoleValue === TopicAccessLevel.COMMENTER}
                                Anyone with the link can comment
                            {:else if specificRoleValue === TopicAccessLevel.EDITOR}
                                Anyone with the link can edit
                            {/if}
                        </div>
                    </div>

                    {#if generalRoleValue > 0}
                        <select
                            class="public-role-settings__public-select"
                            value={specificRoleValue}
                            on:change={specificRoleChanged}
                            disabled={role !== TopicAccessLevel.OWNER}
                        >
                            <option value={TopicAccessLevel.VIEWER}
                                >Viewer</option
                            >
                            <option value={TopicAccessLevel.COMMENTER}
                                >Commenter</option
                            >
                            <option value={TopicAccessLevel.EDITOR}
                                >Editor</option
                            >
                        </select>
                    {/if}
                </div>
            </div>
        </div>

        <footer>
            <ShareInput
                shareButtonClicked={() => dispatch("close")}
                {topic}
                {showShareButton}
                {showDashboardButton}
            ></ShareInput>
        </footer>
    {/if}
</div>

<style>
    @import "../main.css";

    .share-preferences-panel {
        @apply flex h-full w-full flex-col overflow-auto bg-gray-100 !important;
    }

    .share-preferences-panel-content {
        @apply reset flex-1 overflow-auto px-4 pb-8 !important;
    }

    .users {
        @apply mt-4 flex flex-col !important;
    }

    .users-list {
        @apply flex flex-col gap-4 !important;
    }

    .text-area {
        @apply reset whitespace-pre-wrap outline-none w-full resize-y overflow-auto rounded-2xl border border-solid border-gray-200 bg-white px-4 py-2 text-sm !important;
    }
    .text-area:empty:before {
        content: attr(placeholder);
        @apply pointer-events-none block text-gray-400 !important;
    }

    .public-role {
        @apply mb-8 mt-4 flex flex-col !important;
    }
    .public-role-header {
        @apply reset mb-2 flex flex-row items-center justify-between !important;
    }
    .public-role-header__question {
        @apply reset !important;
    }
    .public-role-header__question svg {
        @apply reset h-4 w-4 text-gray-400 hover:text-black !important;
    }
    .public-role-settings {
        @apply flex flex-row items-center gap-[0.3em] rounded-2xl border border-gray-200 bg-white px-4 py-2 !important;
    }
    .public-role-settings__icon svg {
        @apply h-5 w-5 !important;
    }

    .public-role-settings__general {
        @apply flex w-full flex-col !important;
    }
    .public-role-settings__general-select {
        @apply reset appearance-auto text-sm !important;
    }
    .public-role-settings__general-select:disabled {
        @apply reset appearance-none pl-1 !important;
    }
    .public-role-settings__general-info {
        @apply reset pl-1 text-xs text-gray-500 !important;
    }

    .public-role-settings__public-select {
        @apply reset ml-auto appearance-auto truncate bg-white text-right text-xs !important;
    }
    .public-role-settings__public-select:disabled {
        @apply appearance-none bg-white italic !important;
    }

    footer {
        @apply reset z-10 mt-auto flex w-full flex-col items-center p-4 shadow-[0_-30px_30px_0px_rgba(243,244,245,1)] !important;
    }
</style>
